import { addToast, Button } from '@octano/global-ui';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useFormContext } from 'react-hook-form';
import { Col, Row } from 'reactstrap';
import RemoteSearchSelect from '../../../../../components/form/RemoteSearchSelect';
import { SectionTitle } from '../../../../../components/text';
import { useValidations } from '../../../../../hooks/useValidations';
import { useSearchUsers } from '../hooks/useSearchUsers';
import useTexts from '../hooks/useTexts';
import { AddEmailModal, AddEmailModalRef } from '../modals/AddEmailModal';
import { CreateSessionForm } from './FormCreate';

const field1 = 'participant';
const field2 = 'participants';

const RemoteSearchField = ({ disabled = false }: { disabled?: boolean }) => {
  const texts = useTexts();
  const { msgValidations } = useValidations();
  const addEmailRef = useRef<AddEmailModalRef>(null);

  const {
    watch,
    setValue,
    getValues,
    formState: { isSubmitting },
  } = useFormContext<CreateSessionForm>();

  const participant = watch(field1);
  const participants = watch(field2);
  const participantsMemo = useMemo(() => {
    return participants || [];
  }, [participants]);

  const searchStudents = useSearchUsers();

  const handlePushEmail = useCallback(
    (nextAssistant: any) => {
      if (!nextAssistant?.value && !nextAssistant?.email) {
        return;
      }

      const current = getValues(field2) || [];

      const isDuplicate = current.some((i) => {
        // Si tiene `value`, comparar por `value`
        if (nextAssistant?.value) {
          return i.value === nextAssistant.value;
        }
        // Si NO tiene `value`, comparar por `email`
        if (nextAssistant?.email) {
          return i.email?.toLowerCase() === nextAssistant.email.toLowerCase();
        }
        return false;
      });

      // evita duplicados
      if (isDuplicate) {
        addToast({
          icon: 'error',
          color: 'danger',
          text: texts.msg.emailDuplicated,
        });
        return;
      }

      const newParticipants = [...current, nextAssistant];
      setValue(field2, newParticipants, {
        shouldValidate: true,
        shouldDirty: true,
      });
    },
    [getValues, setValue, texts.msg.emailDuplicated],
  );

  const handlePopEmail = useCallback(
    (removeAssistant: any) => {
      const current = getValues(field2) || [];

      const newParticipants = current.filter(
        (participant) => participant.email !== removeAssistant.email,
      );
      setValue(field2, newParticipants, {
        shouldValidate: true,
        shouldDirty: true,
      });
    },
    [getValues, setValue],
  );

  const filterOptions = useCallback(
    (searchText: string) => {
      return searchStudents(searchText, participantsMemo);
    },
    [participantsMemo, searchStudents],
  );

  const onClickAdd = useCallback(() => {
    if (isSubmitting) {
      return;
    }
    addEmailRef?.current?.openModal();
  }, [isSubmitting]);

  useEffect(() => {
    setValue(field2, participantsMemo);
  }, [setValue, participantsMemo]);

  return (
    <>
      <Row className="pb-5">
        <Col xs={12} className="pb-4">
          <SectionTitle text={texts.form.assistants} />
        </Col>
        <Col xs={12} md={9}>
          <RemoteSearchSelect
            key={participantsMemo.length}
            name={field1}
            value={participant ?? { value: '', label: '' }}
            label={texts.form.assistantsSearch}
            filterOptions={filterOptions}
            onChange={(next) => {
              handlePushEmail(next);
            }}
            rules={{ required: msgValidations.required }}
            shouldUnregister
            disabled={disabled}
          />
        </Col>
        <Col xs={12} md={3}>
          <Button
            text={texts.actions.addEmail}
            icon="plus"
            style={{ fontWeight: 'bold' }}
            onClick={onClickAdd}
            disabled={isSubmitting || disabled}
            outlined
            fullwidth
          />
        </Col>
        <Col xs={12} md={12}>
          <div className="d-flex flex-wrap">
            {participantsMemo.map((a, i: number) => (
              <button
                key={i}
                type="button"
                className="btn bg-tertiary text-primary border border-primary rounded mr-2 mb-2"
                onClick={() => !disabled && handlePopEmail(a)}
              >
                {`${a?.name} (${a?.email}) X`}
              </button>
            ))}
          </div>
        </Col>
      </Row>
      <AddEmailModal
        ref={addEmailRef}
        list={participantsMemo as any}
        onConfirm={handlePushEmail}
      />
    </>
  );
};

export default RemoteSearchField;
